import { useState } from "react";
import { ChevronDown, ChevronRight, Wind, Fuel, Zap, Database, Filter, Thermometer, Cloud, Factory } from "lucide-react";

const IggEmissionAnalytics = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const calculations = [
    {
      id: "igg-co2-emission",
      title: "IGG CO₂ Emission Calculation",
      description: "Calculates CO₂ emissions for each fuel type using CF_CO₂ factors from FuelTable for IGG operations",
      formula: "CO₂ = Fuel Consumption (kg) × CF_CO₂ (kg/kg-fuel)",
      subFormulas: [
        "CO₂_HFO = iggConsHfo × CF_CO₂_HFO",
        "CO₂_LFO = iggConsLfo × CF_CO₂_LFO", 
        "CO₂_MGO = iggConsMgo × CF_CO₂_MGO",
        "Total IGG CO₂ = CO₂_HFO + CO₂_LFO + CO₂_MGO"
      ],
      icon: <Cloud className="w-5 h-5" />,
      borderColor: "border-blue-200",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
    },
    {
      id: "igg-ch4-emission",
      title: "IGG Methane (CH₄) Emission Calculation",
      description: "Calculates CH₄ emissions for each fuel type using CF_CH₄ factors from FuelTable for IGG",
      formula: "CH₄ = Fuel Consumption (kg) × CF_CH₄ (kg/kg-fuel)",
      subFormulas: [
        "CH₄_HFO = iggConsHfo × CF_CH₄_HFO",
        "CH₄_LFO = iggConsLfo × CF_CH₄_LFO",
        "CH₄_MGO = iggConsMgo × CF_CH₄_MGO",
        "Total IGG CH₄ = CH₄_HFO + CH₄_LFO + CH₄_MGO"
      ],
      icon: <Factory className="w-5 h-5" />,
      borderColor: "border-purple-200",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    },
    {
      id: "igg-n2o-emission",
      title: "IGG Nitrous Oxide (N₂O) Emission Calculation",
      description: "Calculates N₂O emissions for each fuel type using CF_N₂O factors from FuelTable for IGG",
      formula: "N₂O = Fuel Consumption (kg) × CF_N₂O (kg/kg-fuel)",
      subFormulas: [
        "N₂O_HFO = iggConsHfo × CF_N₂O_HFO",
        "N₂O_LFO = iggConsLfo × CF_N₂O_LFO",
        "N₂O_MGO = iggConsMgo × CF_N₂O_MGO",
        "Total IGG N₂O = N₂O_HFO + N₂O_LFO + N₂O_MGO"
      ],
      icon: <Thermometer className="w-5 h-5" />,
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
    {
      id: "igg-well-to-tank-calculation",
      title: "IGG Well-to-Tank (WtT) Calculation",
      description: "Calculates upstream emissions from IGG fuel production and transportation",
      formula: "WtT = Σ(Fuel Mass × LCV × WtT × RWD) / Total Energy Input",
      subFormulas: [
        "Fuel Mass (g) = IGG Consumption (kg) × 1,000,000",
        "Total Energy = Σ(Fuel Mass × LCV × RWD) + Shore Power Energy (MJ)",
        "Shore Power from VesselPerformance.shorePowerEnergy",
        "RWD = 2.0 for RFNBO fuels, 1.0 otherwise"
      ],
      icon: <Fuel className="w-5 h-5" />,
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },
    {
      id: "igg-tank-to-wake-calculation",
      title: "IGG Tank-to-Wake (TtW) Calculation",
      description: "Calculates combustion emissions including methane slip for Inert Gas Generator",
      formula: "TtW = Σ[Fuel Mass × ((1-CSlip%)×CO₂eq_TtW + (CSlip%×28))] / Total Energy Input",
      subFormulas: [
        "CO₂eq_TtW = (CF_CO₂ × GWP_CO₂) + (CF_CH₄ × GWP_CH₄) + (CF_N₂O × GWP_N₂O)",
        "GWP_CO₂ = 1, GWP_CH₄ = 28, GWP_N₂O = 298",
        "CSlip = Carbon slip percentage for each fuel",
        "Shore Power Energy included in denominator"
      ],
      icon: <Zap className="w-5 h-5" />,
      borderColor: "border-red-200",
      bgColor: "bg-red-50",
      textColor: "text-red-700",
    },
    {
      id: "igg-ghg-intensity-calculation",
      title: "IGG GHG Intensity Calculation",
      description: "Calculates total GHG intensity in g CO₂e/MJ including renewable fuel rewards",
      formula: "GHG Intensity = Reward Factor × (WtT + TtW)",
      subFormulas: [
        "Reward Factor Wind = 1.0 (default)",
        "IGG GHG Intensity = 1.0 × (WtT + TtW)",
        "Units: g CO₂e/MJ"
      ],
      icon: <Zap className="w-5 h-5" />,
      borderColor: "border-indigo-200",
      bgColor: "bg-indigo-50",
      textColor: "text-indigo-700",
    },
    {
      id: "igg-conversion-to-mt",
      title: "IGG Conversion to Metric Tons (MT)",
      description: "Converts IGG emissions from kilograms to metric tons for reporting",
      formula: "IGG Emissions (MT/day) = IGG Emissions (kg) ÷ 1000",
      subFormulas: [
        "IGG CO₂_MT = Total IGG CO₂ (kg) ÷ 1000",
        "IGG CH₄_MT = Total IGG CH₄ (kg) ÷ 1000",
        "IGG N₂O_MT = Total IGG N₂O (kg) ÷ 1000",
        "GHG reported in g CO₂e/MJ (no conversion needed)"
      ],
      icon: <Database className="w-5 h-5" />,
      borderColor: "border-gray-200",
      bgColor: "bg-gray-50",
      textColor: "text-gray-700",
    }
  ];

  const constants = [
    {
      name: "Global Warming Potentials (GWP)",
      values: [
        "GWP_CO₂ = 1",
        "GWP_CH₄ = 28", 
        "GWP_N₂O = 298"
      ]
    },
    {
      name: "Lower Calorific Values (LCV)",
      values: [
        "LCV_HFO = 40.5 MJ/kg (default)",
        "LCV_LFO = 41.5 MJ/kg (default)",
        "LCV_MGO = 42.7 MJ/kg (default)"
      ]
    },
    {
      name: "Reward Factors & Parameters",
      values: [
        "Reward Factor Wind = 1.0",
        "RFNBO RWD = 2.0",
        "Non-RFNBO RWD = 1.0",
        "Mass Conversion: 1 kg = 1,000,000 g"
      ]
    }
  ];

  const dataSources = [
    {
      collection: "VesselEmissionRawData",
      fields: [
        "iggConsHfo (kg) - Inert Gas Generator HFO consumption",
        "iggConsLfo (kg) - Inert Gas Generator LFO consumption",
        "iggConsMgo (kg) - Inert Gas Generator MGO consumption",
        "reportDate",
        "vesselId",
        "voyageId"
      ]
    },
    {
      collection: "FuelTable",
      fields: [
        "CF_CO₂, CF_CH₄, CF_N₂O (emission factors)",
        "LCV (Lower Calorific Value)",
        "WtT (Well-to-Tank factor)",
        "CSlip (Carbon Slip %)",
        "Fuel_Class (RFNBO classification)"
      ],
      fuelTypes: ["HFO", "LFO", "MGO"]
    },
    {
      collection: "VesselPerformance",
      fields: [
        "shorePowerEnergy (MJ)",
        "recordedAt"
      ]
    }
  ];

  const validationChecks = [
    "Emission factor validation for HFO, LFO, MGO",
    "Required fields: CF_C02, CF_CH4, CF_N20 must exist in FuelTable",
    "Missing factors trigger error response",
    "Data completeness check: hasActualEmissionData flag"
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#faeebe] rounded-lg">
              <Wind className="w-6 h-6 text-[#b7950b]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                IGG Emission Calculations
              </h2>
              <p className="text-gray-600 text-sm">
                Inert Gas Generator greenhouse gas analysis methodology
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">IGG Data Sources</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {dataSources.map((source, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-gray-800 mb-2">{source.collection}</h4>
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-gray-700">Fields:</div>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {source.fields.map((field, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-gray-400">•</span>
                            <span className="font-mono text-xs">{field}</span>
                          </li>
                        ))}
                      </ul>
                      {source.fuelTypes && (
                        <>
                          <div className="text-sm font-medium text-gray-700 mt-3">Fuel Types:</div>
                          <div className="flex flex-wrap gap-2">
                            {source.fuelTypes.map((type, idx) => (
                              <span key={idx} className="px-2 py-1 bg-white text-xs rounded border">
                                {type}
                              </span>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Detailed Calculations</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {calculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div>
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Primary Formula:</p>
                        <p className="text-sm font-semibold bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </p>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">IGG-Specific Breakdown:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IggEmissionAnalytics;